import React, { Fragment, useEffect, useState } from 'react';
import { Dialog, Transition } from '@headlessui/react';
import { documentWorkflowService, type ExecuteWorkflowTransitionResult } from '../../../services/documentWorkflowService';
import { WorkflowActionCodes } from '../../../utils/workflowConstants';

export interface WorkflowTransitionModalProps {
  isOpen: boolean;
  onClose: () => void;
  documentId: string;
  action: string;
  requiresComment: boolean;
  onSuccess: (result: ExecuteWorkflowTransitionResult) => void;
  /**
   * Chamado após validar o comentário e antes da transição.
   * Retorne `false` para abortar a execução (o modal fecha; o pai assume o fluxo).
   */
  onBeforeExecute?: (comment: string | undefined) => Promise<boolean>;
}

const ACTION_TITLES: Record<string, string> = {
  SendForReview: 'Enviar para revisão',
  Approve: 'Aprovar',
  Reject: 'Rejeitar',
  Publish: 'Publicar',
  Archive: 'Arquivar',
  SendForSignature: 'Enviar para assinatura',
  Sign: 'Assinar',
  CompleteEdit: 'Avançar para próxima etapa de edição',
  Edit: 'Confirmar edição / reativação',
};

export const WorkflowTransitionModal: React.FC<WorkflowTransitionModalProps> = ({
  isOpen,
  onClose,
  documentId,
  action,
  requiresComment,
  onSuccess,
  onBeforeExecute,
}) => {
  const [comment, setComment] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (isOpen) {
      setComment('');
      setError('');
      setLoading(false);
    }
  }, [isOpen, action, documentId]);

  const title = ACTION_TITLES[action] ?? 'Confirmar ação';

  const handleConfirm = async () => {
    const trimmed = comment.trim();
    if (requiresComment && !trimmed) {
      setError('Comentário é obrigatório para esta etapa.');
      return;
    }

    setLoading(true);
    setError('');
    try {
      const commentValue = trimmed || undefined;

      if (onBeforeExecute)
      {
        const shouldContinue = await onBeforeExecute(commentValue);
        if (!shouldContinue)
        {
          onClose();
          return;
        }
      }

      const result =
        action === 'Approve'
          ? await documentWorkflowService.approve(documentId, commentValue)
          : action === 'Sign'
            ? await documentWorkflowService.executeTransition(
                documentId,
                WorkflowActionCodes.Sign,
                commentValue
              )
            : await documentWorkflowService.executeTransition(
                documentId,
                action,
                commentValue
              );
      if (!result.success) {
        setError(result.errorMessage || 'Não foi possível concluir a transição.');
        return;
      }
      onClose();
      onSuccess(result);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Erro ao executar transição.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Transition appear show={isOpen} as={Fragment}>
      <Dialog as="div" className="relative z-50" onClose={loading ? () => {} : onClose}>
        <Transition.Child
          as={Fragment}
          enter="ease-out duration-200"
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave="ease-in duration-150"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          <div className="fixed inset-0 bg-black/30" aria-hidden="true" />
        </Transition.Child>

        <div className="fixed inset-0 overflow-y-auto">
          <div className="flex min-h-full items-center justify-center p-4">
            <Transition.Child
              as={Fragment}
              enter="ease-out duration-200"
              enterFrom="opacity-0 scale-95"
              enterTo="opacity-100 scale-100"
              leave="ease-in duration-150"
              leaveFrom="opacity-100 scale-100"
              leaveTo="opacity-0 scale-95"
            >
              <Dialog.Panel className="w-full max-w-lg transform overflow-hidden rounded-lg bg-white p-6 shadow-xl transition-all">
                <Dialog.Title className="text-lg font-semibold text-gray-900">{title}</Dialog.Title>

                <div className="mt-4">
                  <label htmlFor="workflow-transition-comment" className="block text-sm font-medium text-gray-700">
                    Comentário {requiresComment ? '(obrigatório)' : '(opcional)'}
                  </label>
                  <textarea
                    id="workflow-transition-comment"
                    rows={4}
                    value={comment}
                    onChange={e => setComment(e.target.value)}
                    disabled={loading}
                    className="mt-1 block w-full rounded-md border border-gray-300 px-3 py-2 text-sm shadow-sm focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500 disabled:bg-gray-100"
                    placeholder={requiresComment ? 'Descreva o motivo ou observações…' : 'Observações opcionais…'}
                  />
                </div>

                {error ? (
                  <p className="mt-3 text-sm text-red-600" role="alert">
                    {error}
                  </p>
                ) : null}

                <div className="mt-6 flex justify-end gap-3">
                  <button
                    type="button"
                    onClick={onClose}
                    disabled={loading}
                    className="rounded-md border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700 shadow-sm hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-60"
                  >
                    Cancelar
                  </button>
                  <button
                    type="button"
                    onClick={() => void handleConfirm()}
                    disabled={loading}
                    className="inline-flex items-center rounded-md border border-transparent bg-blue-600 px-4 py-2 text-sm font-medium text-white shadow-sm hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 disabled:cursor-not-allowed disabled:bg-blue-400"
                  >
                    {loading ? (
                      <>
                        <span className="mr-2 inline-block h-4 w-4 animate-spin rounded-full border-2 border-white border-t-transparent" />
                        Aguarde…
                      </>
                    ) : (
                      'Confirmar'
                    )}
                  </button>
                </div>
              </Dialog.Panel>
            </Transition.Child>
          </div>
        </div>
      </Dialog>
    </Transition>
  );
};
